import {ActivityIndicator, StyleSheet, Text, View} from "react-native";
import {EventFollowRequest, Participating} from "@/lib/types";
import {useRouter} from "expo-router";
import {fetchBackendNoContent} from "@/lib/backend";
import {useSession} from "@/lib/auth/auth";
import Page from "@/lib/components/Page";
import Card from "@/lib/components/Card";
import ErrorPage from "@/lib/components/ErrorPage";
import SpinnerPage from "@/lib/components/SpinnerPage";
import {Color} from "@/lib/styles";
import ActionButton from "@/lib/components/ActionButton";
import AvatarBox from "@/lib/components/AvatarBox";
import Timestamp from "@/lib/components/Timestamp";
import Location from "@/lib/components/Location";
import Header from "@/lib/components/Header";
import NavButton from "@/lib/components/NavButton";
import {useEffect, useMemo, useState} from "react";
import ErrorMessage from "@/lib/components/ErrorMessage";
import {useActivity} from "@/lib/components/ActivityProvider";
import {useEvent} from "@/lib/components/EventProvider";
import ChatPreviewBox from "../../../lib/components/ChatPreviewBox";
import ChatInput from "@/lib/components/ChatInput";

export default function EventPage() {
    const {session} = useSession();
    const router = useRouter();
    const [busy, setBusy] = useState<EventFollowRequest | null>(null);
    const [saveError, setSaveError] = useState<Error | null>(null);
    const [sendingMessage, setSendingMessage] = useState(false);
    const {refreshActivities} = useActivity();

    const {event, eventLoading, eventError, refreshEvent, eventChatIds, refreshEventChats} = useEvent();

    const definitelyAttending = useMemo(() => {
        if (!event) {
            return [];
        }
        return event.participants.filter(p => (
            p.status === Participating.HOST ||
            p.status === Participating.ATTENDING ||
            p.status === Participating.MODERATOR
        ));
    }, [event]);

    const hosts = useMemo(() => event?.participants.filter(p => p.status === Participating.HOST) || [], [event]);
    const numInvited = useMemo(() => event?.participants.filter(p => p.status === Participating.INVITED).length || 0, [event]);
    const numFollowing = useMemo(() => event?.participants.filter(p => p.status === Participating.FOLLOWING).length || 0, [event]);

    const infoLines = useMemo(() => {
        if (!event || !event.info) {
            return [];
        }
        return event.info.split(/\n\n+/);
    }, [event])

    // clear notifications for this event when loaded
    useEffect(() => {
        if (!event) {
            return;
        }
        refreshActivities();
    }, [event, refreshActivities]);

    if (eventError) {
        return <ErrorPage error={eventError}/>
    } else if (eventLoading || !event) {
        return <SpinnerPage/>
    }

    async function saveAttendance(request: EventFollowRequest) {
        if (!event) {
            return;
        }
        setBusy(request);
        fetchBackendNoContent(`/event/${event.event_id}/attend`, "POST", session, request).then(async () => {
            refreshEvent();
            setBusy(null);
        }).catch((error) => {
            setSaveError(error);
        }).finally(() => {
            setBusy(null);
        });
    }

    const isEditor = event.permissions.edit;
    const isAttending = event.participation_status === Participating.ATTENDING;
    const isNotAttending = event.participation_status === Participating.NOTATTENDING;
    const canAttend = event.permissions.participate && !isEditor && !isNotAttending;
    const canNotAttend = event.permissions.participate && !isEditor && !isAttending;

    return (
        <Page scrollable={true}>
            {event.invited_by && event.participation_status === "invited" &&
                <AvatarBox size={48} border={false} avatars={[event.invited_by]} color={event.color}>
                    <Text style={styles.invitedByText}>{event.invited_by.gecos} har invitert deg</Text>
                </AvatarBox>
            }

            <Card image_url={event.image_url}
                  color={event.color}
                  contrast_color={event.contrast_color}
                  title={event.title}
                  strikethrough={event.cancelled}
            >
                <AvatarBox border={false} avatars={hosts.map(host => host.avatar)} color={event.color}>
                    <Text style={styles.arrangedByText}>Arrangeres av {hosts.map((host) => host.avatar.gecos).join(", ")}</Text>
                </AvatarBox>

                {event.cancelled && <Header title={"Eventet er avlyst!"} icon={"cancel"}/>}

                <Timestamp strikethrough={event.cancelled} start={new Date(event.start)} end={event.end ? new Date(event.end) : null}/>

                {event.location && <Location>{event.location}</Location>}

                {session && (canAttend || canNotAttend) &&
                    <View style={{flexDirection: "row-reverse", justifyContent: "flex-start", gap: 8}}>
                        {canAttend && <ActionButton title={isAttending ? "Du har meldt at du kommer" : "Jeg kommer"}
                                                    color={Color.Green}
                                                    busy={busy?.attending !== undefined}
                                                    disabled={!!busy}
                                                    activated={!isAttending}
                                                    icon={isAttending ? "checkmark" : undefined}
                                                    onPress={async () => {
                                                        await saveAttendance({attending: !isAttending});
                                                    }}/>
                        }
                        {canNotAttend && <ActionButton title={isNotAttending ? "Du har meldt avbud" : "Kan ikke"}
                                                       color={Color.PrimaryText}
                                                       busy={busy?.not_attending !== undefined}
                                                       disabled={!!busy}
                                                       icon={isNotAttending ? "cancel" : undefined}
                                                       onPress={async () => {
                                                           await saveAttendance({not_attending: !isNotAttending});
                                                       }}/>
                        }

                    </View>
                }

            </Card>

            {
                saveError && <ErrorMessage error={saveError}/>
            }

            {
                !!session &&
                <>
                    <Header title={`Deltakere ( ${definitelyAttending.length} )`} marginTop={6}/>
                    <AvatarBox avatars={definitelyAttending.map(participant => participant.avatar)}
                               color={event.color}
                               onPress={() => {
                                   router.navigate(`/e/${event.event_id}/participants`);
                               }}
                    >
                        <View style={styles.extraInvited}>
                            {numFollowing > 0 && <Text style={styles.extraInvitedText}>+ {numFollowing} interessert</Text>}
                            {numInvited > 0 && <Text style={styles.extraInvitedText}>+ {numInvited} invitert</Text>}
                        </View>
                    </AvatarBox>
                </>
            }
            {
                !session &&
                <NavButton title={"Logg inn for å følge eventet og deltakerne"} href={`/u/account/login`}
                           icon={"relation"}/>
            }
            {
                !!session &&
                <Header title={`Chat ( ${eventChatIds?.length || 0} )`}/>
            }
            {
                event.permissions.discuss &&
                <ChatInput
                    event_id={event.event_id}
                    onMessageSubmit={()=>{
                        setSendingMessage(true);
                    }}
                    onRoundTrip={() => {
                        setSendingMessage(false);
                        refreshEventChats();
                    }}/>
            }
            {
                sendingMessage &&
                <View style={{gap: 8}}>
                    <ActivityIndicator size={"small"} color={Color.PrimaryText}/>
                </View>
            }
            {
                eventChatIds !== undefined &&
                <View style={{gap: 8}}>
                    {eventChatIds.map(id => <ChatPreviewBox key={id} chat_id={id} color={event.color}/>)}
                </View>
            }
            {
                event.info ?
                    <View style={styles.infoContainer}>
                        <Header title={"Info om arrangementet"}/>
                        {infoLines.map((line, key) =>
                            <Text style={styles.infoText}
                                  key={key}
                                  lineBreakStrategyIOS={"push-out"} // unngå horunger
                                  selectable={true}
                            >{line}</Text>
                        )}
                    </View>
                    :
                    <View style={styles.infoContainer}>
                        <Header title={"Arrangementet har ingen ytterligere beskrivelse."}/>
                    </View>
            }
        </Page>
    )
}

export const styles = StyleSheet.create({
    infoContainer: {
        width: "100%",
        marginTop: 12,
    },
    infoText: {
        color: Color.PrimaryText,
        marginTop: 8,
        fontSize: 18,
        lineHeight: 26,
        paddingRight: 16,
    },
    extraInvited: {
        flexDirection: "column",
        flexWrap: "nowrap",
        gap: 4,
        justifyContent: "center",
    },
    extraInvitedText: {
        color: Color.PrimaryText,
        fontSize: 12,
        lineHeight: 12,
        fontWeight: 500,
    },
    invitedByText: {
        color: Color.PrimaryText,
        fontWeight: 500,
        fontSize: 18,
    },
    arrangedByText: {
        color: Color.PrimaryText,
        fontSize: 16,
    }
})